import { createSelector } from 'reselect';

// Other selectors
import { getUser } from '../auth/selectors';

const getCurrentlyLoadedHome = state => {
    if (state.homes.getIn(['detail', 'data'])) {
        return state.homes.getIn(['detail', 'data']);
    }
    return undefined;
};

const getList = state => {
    if (state.homes.get('list')) {
        return state.homes.getIn(['list', 'data']);
    }
    return undefined;
};

const getAdminHomeList = state => {
    if (state.homes.getIn(['administration', 'homes'])) {
        return state.homes.getIn(['administration', 'homes', 'data']);
    }
    return undefined;
};

const getAdminSearchHomeFields = state => {
    if (state.homes.get('administration')) {
        return state.homes.getIn(['administration', 'searchHomeFields']);
    }
    return {};
};
const getAdminSearchUserFields = state => {
    if (state.homes.get('administration')) {
        return state.homes.getIn(['administration', 'searchUserFields']);
    }
    return {};
};

const getAdminHomesAreLoading = state => state.homes.getIn(['administration', 'homes', 'isLoading']);

const getAdminUsersList = state => {
    if (state.homes.getIn(['administration', 'users'])) {
        return state.homes.getIn(['administration', 'users', 'data']);
    }
    return undefined;
};

const getAdminUsersAreLoading = state => state.homes.getIn(['administration', 'users', 'isLoading']);

const getInvitedUsersData = state => state.homes.getIn(['detail', 'users', 'invitedUsers', 'data']);
const getInvitedUsersIsLoading = state =>
    state.homes.getIn(['detail', 'users', 'invitedUsers', 'isLoading']);

// Meters
const getMetersData = state => {
    if (state.homes.getIn(['detail', 'meters'])) {
        return state.homes.getIn(['detail', 'meters', 'data']);
    }
    return undefined;
};

const getMetersAreLoading = state => state.homes.getIn(['detail', 'meters', 'isLoading']);

const getMeterModalIsLoading = state =>
    state.homes.getIn(['detail', 'meters', 'addOrEditModal', 'isLoading']);

const getSwitchModalIsShown = state =>
    state.homes.getIn(['detail', 'switch', 'addOrEditModal', 'isShown']);

const getSwitchModalIsLoading = state =>
    state.homes.getIn(['detail', 'switch', 'addOrEditModal', 'isLoading']);

const getDeviceModalIsShown = state => state.homes.getIn(['detail', 'addOrEditDeviceModal', 'isShown']);

const getDeviceModalIsLoading = state =>
    state.homes.getIn(['detail', 'addOrEditDeviceModal', 'isLoading']);

const getHomeModalIsShown = state => state.homes.getIn(['addOrEditModal', 'isShown']);

const getHomeModalIsLoading = state => state.homes.getIn(['addOrEditModal', 'isLoading']);

const getHomeModalIsEdit = state => state.homes.getIn(['addOrEditModal', 'isEdit']);

const getHomeModalSelectedHome = state => state.homes.getIn(['addOrEditModal', 'record']);

const getOwnersListIsLoading = state => state.homes.getIn(['ownerList', 'isLoading']);

const getOwnersListData = state => state.homes.getIn(['ownerList', 'data']);

/* ====================== SELECTORS ======================== */
export const getHomesList = createSelector(
    [getList],
    homesListData => homesListData && homesListData.toJS()
);

export const getAllHomes = createSelector([getAdminHomeList], adminHomes => adminHomes);

export const getSearchHomeFields = createSelector([getAdminSearchHomeFields], fields => fields);
export const getSearchUserFields = createSelector([getAdminSearchUserFields], fields => fields);

export const getAllHomesAreLoading = createSelector([getAdminHomesAreLoading], isLoading => isLoading);

export const getAllUsers = createSelector([getAdminUsersList], userListData => {
    if (!(userListData && userListData.size > 0 && userListData.get('content'))) return undefined;

    const userListDataContentSorted = userListData.get('content').sort((ownerA, ownerB) => {
        const surnameDiff =
            (ownerA.get('surename') > ownerB.get('surename')) -
            (ownerA.get('surename') < ownerB.get('surename'));
        return surnameDiff === 0
            ? (ownerA.get('name') > ownerB.get('name')) - (ownerA.get('name') < ownerB.get('name'))
            : surnameDiff;
    });

    return userListData.set('content', userListDataContentSorted);
});

export const getAllUsersAreLoading = createSelector([getAdminUsersAreLoading], isLoading => isLoading);

export const isOwnerOfCurrentlyLoadedHouse = createSelector(
    [getCurrentlyLoadedHome, getUser],
    (currentlyLoadedHouse, userData) => {
        if (!currentlyLoadedHouse || !userData) return false;
        return currentlyLoadedHouse.getIn(['ownerUser', 'id']) === userData.get('id');
    }
);

export const getInvitedUsersList = createSelector([getInvitedUsersData], invitedUsers => {
    if (invitedUsers && invitedUsers.size > 0) {
        return invitedUsers.filter(
            invitedUser => invitedUser.get('status') === 'SENT' && !invitedUser.get('deleted')
        );
    }
    return null;
});

export const getInvitedUsersAreLoading = createSelector(
    [getInvitedUsersIsLoading],
    isLoading => isLoading
);

// Meters
export const getHomeDetailMetersData = createSelector([getMetersData], metersData => {
    if (metersData && metersData.size > 0) {
        return metersData.toJS();
    }
    return null;
});

export const getHomeDetailMetersAreLoading = createSelector(
    [getMetersAreLoading],
    isLoading => isLoading
);

export const getAddOrEditMeterModalIsLoading = createSelector(
    [getMeterModalIsLoading],
    isLoading => isLoading
);

export const getAddOrEditSwitchModalIsShown = createSelector([getSwitchModalIsShown], isShown => isShown);

export const getAddOrEditSwitchModalIsLoading = createSelector(
    [getSwitchModalIsLoading],
    isLoading => isLoading
);

export const getAddOrEditDeviceModalIsShown = createSelector(
    [getDeviceModalIsShown],
    isShown => isShown
);

export const getAddOrEditDeviceModalIsLoading = createSelector(
    [getDeviceModalIsLoading],
    isLoading => isLoading
);

// --> Add or Edit HOME modal
export const getAddOrEditHomeModalIsShown = createSelector([getHomeModalIsShown], isShown => isShown);
export const getAddOrEditHomeModalIsLoading = createSelector(
    [getHomeModalIsLoading],
    isLoading => isLoading
);
export const getAddOrEditHomeModalIsEdit = createSelector([getHomeModalIsEdit], isEdit => isEdit);
export const getAddOrEditHomeModalSelectedHome = createSelector(
    [getHomeModalSelectedHome],
    selectedHome => selectedHome
);

// --> Owners list
export const getHomeOwnersListIsLoading = createSelector(
    [getOwnersListIsLoading],
    isLoading => isLoading
);

export const getHomeOwnersListData = createSelector([getOwnersListData], ownersList => ownersList);
